import { useMemo } from 'react';
import { BODY_PAINT_STATE, REPAIR_PROGRESS_STATE } from '@/consts/bodyAndPaint';

interface RepairDetailInfoHookProps {
  status: number;
  pickUpDate: Date | undefined;
}
export const useRepairDetailStatusInfo = ({
  status,
  pickUpDate,
}: RepairDetailInfoHookProps) => {
  const RepairStateMap: Record<
    keyof typeof BODY_PAINT_STATE,
    typeof REPAIR_PROGRESS_STATE[number]
  > = useMemo(() => {
    return {
      ACCIDENT_ACCEPT: 'CHECK_IN_ACCEPTED',
      CHECK_IN_ACCEPT: 'REPAIR_ESTIMATING',
      ESTIMATE_APPLY: 'ESTIMATING_HAS_DONE',
      INSURANCE_APPLY: 'REPAIR_ESTIMATING',
      DETERMINED_APPLIES: 'REPAIR_ESTIMATING',
      APPLIES_RESULT: 'ESTIMATING_HAS_DONE',
      REPAIR_CLAIM: 'REPAIR_EXECUTING',
      NO_REPAIR: 'REPAIR_CANCELED',
      DISASSEMBLY: 'REPAIR_EXECUTING',
      REPAIR_HAS_DONE: 'REPAIR_EXECUTING',
      INSPECTION_HAS_DONE: 'REPAIR_HAS_DONE',
      DELIVERED_HAS_DONE: 'DELIVERED_HAS_DONE',
      REPAIR_CANCELLED: 'REPAIR_CANCELED',
      PICK_UP_PRE_CONFIRMED: 'PICK_UP_PRE_CONFIRMED',
      PICK_UP_CONFIRMED: 'PICK_UP_CONFIRMED',
    };
  }, []);
  const state = useMemo(
    () => Object.values(RepairStateMap)[status],
    [status, RepairStateMap],
  );

  const statusClass: Record<typeof REPAIR_PROGRESS_STATE[number], string> = {
    CHECK_IN_ACCEPTED: 'check-in-accepted',
    CHECK_IN_COMPLETED: 'check-in-completed',
    REPAIR_CANCELED: 'check-in-canceled',
    REPAIR_ESTIMATING: 'repair-estimating',
    ESTIMATING_HAS_DONE: 'repair-estimating',
    REPAIR_EXECUTING: 'repair-executing',
    REPAIR_HAS_DONE: 'repair-has-done',
    DELIVERED_HAS_DONE: 'delivered-has-done',
    PICK_UP_PRE_CONFIRMED: 'repair-has-done',
    PICK_UP_CONFIRMED: 'repair-has-done',
  };
  const header: Record<typeof REPAIR_PROGRESS_STATE[number], string> = {
    CHECK_IN_ACCEPTED: '入庫予約を受け付けました',
    CHECK_IN_COMPLETED: '入庫予約が完了しました',
    REPAIR_CANCELED: 'ご予約を取り消しました',
    REPAIR_ESTIMATING: '見積もり中',
    ESTIMATING_HAS_DONE: '見積もり中',
    REPAIR_EXECUTING: '修理中',
    REPAIR_HAS_DONE: '修理完了',
    DELIVERED_HAS_DONE: '納車済み',
    PICK_UP_PRE_CONFIRMED: '修理完了',
    PICK_UP_CONFIRMED: '修理完了',
  };
  const body: Record<typeof REPAIR_PROGRESS_STATE[number], string[]> = {
    CHECK_IN_ACCEPTED: [
      'ただいま予約状況を確認しておりますので',
      '入庫日確定までしばらくお待ちください。',
    ],
    CHECK_IN_COMPLETED: ['入庫日が確定しました。'],
    REPAIR_CANCELED: ['またのご利用をお待ちしております。'],
    REPAIR_ESTIMATING: [
      '工場への搬送が完了いたしました。',
      '見積書が届くまで今しばらくお待ちください。',
    ],
    ESTIMATING_HAS_DONE: [
      '工場への搬送が完了いたしました。',
      '見積書が届くまで今しばらくお待ちください。',
    ],
    REPAIR_EXECUTING: [
      'お客様のご依頼内容を確認し、',
      '工場にて着工開始致しました。',
      '修理が終わるまで今しばらくお待ちください。',
    ],
    REPAIR_HAS_DONE: pickUpDate
      ? [
          'ご返却日の予約を受け付けました',
          '当日になりましたら、工場にお越しください。',
        ]
      : [
          '工場より修理完了のご報告がございました。',
          'お車のご返却可能日につきましては、',
          'こちらからご予約をお願いします。',
        ],
    DELIVERED_HAS_DONE: [
      'この度はご利用いただき',
      '誠にありがとうございました。',
    ],
    PICK_UP_PRE_CONFIRMED: [
      'ご返却日の予約を受け付けました',
      '当日になりましたら、工場にお越しください。',
    ],
    PICK_UP_CONFIRMED: [
      'ご返却日が確定しました。',
      '当日になりましたら、工場にお越しください。',
    ],
  };
  return {
    currentState: state,
    statusClass: statusClass[state],
    headerText: header[state],
    bodyText: body[state],
  };
};
